import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, throwError } from 'rxjs';
import { catchError, pluck, switchMap, tap } from 'rxjs/operators';
import { APP_CONFIG } from 'src/app/core/services/config.service';
import { NavigationService } from 'src/app/core/services/navigation.service';
import { IpInfo } from 'src/app/core/types';
import { FacebookService } from './facebook.service';
import { GoogleService } from './google.service';
import { TransService } from './trans.service';

@Injectable({
  providedIn: 'root',
})
export class AuthorizationService {
  public emailUsedForLogin: string;
  private url = 'http://localhost:8080/api/';
  private ipInfoToken = '';

  constructor(
    private http: HttpClient,
    private facebookService: FacebookService,
    private googleService: GoogleService,
    private transService: TransService,
    private ngZone: NgZone,
    private translate: TranslateService,
    private navigationService: NavigationService,
  ) {
    APP_CONFIG()
      .pipe(pluck('baseUri'))
      .subscribe((url: string) => (this.url = url));

    APP_CONFIG()
      .pipe(pluck('ipinfo'))
      .subscribe((token: string) => (this.ipInfoToken = token));
  }

  public isLoggedIn(): boolean {
    return !!localStorage.token;
  }

  public storeUserType(userType: string): void {
    localStorage.setItem('userType', userType);
  }

  public getUserType(): string | null {
    return localStorage.getItem('userType');
  }

  public clearUserType(): void {
    localStorage.removeItem('userType');
  }

  public getCountryFromIp(): Observable<any> {
    return this.http.get(`https://ipinfo.io?token=${this.ipInfoToken}`).pipe(
      tap((data: IpInfo) => this.handleSuccessReturn(data)),
      catchError((error: HttpErrorResponse) => this.handleError(error)),
    );
  }

  public logout(): Observable<any> {
    this.clearUserType();
    return this.http.post(this.url + 'auth/logout', {}).pipe(
      tap((data) => this.handleSuccess(data)),
      catchError((error) => this.handleError(error)),
    );
  }

  public loginWithEmail(loginData): Observable<any> {
    this.emailUsedForLogin = loginData.email;
    return this.http.post(this.url + 'auth/login', { data: loginData }).pipe(
      tap(() => this.storeUserType('email')),
      tap((data) => this.handleSuccess(data)),
      catchError((error) => this.handleError(error)),
    );
  }

  public loginWithFacebook(): Observable<any> {
    return this.facebookService.login().pipe(
      switchMap((data: any) =>
        this.http
          .post(this.url + 'auth/facebook', {
            data: data?.authResponse?.accessToken,
          })
          .pipe(
            tap(() => this.storeUserType('facebook')),
            tap((response) => this.ngZone.run(() => this.handleSuccess(response))),
          ),
      ),
    );
  }

  public loginWithGoogle(): Observable<any> {
    return this.googleService.login().pipe(
      switchMap((data: any) =>
        this.http.post(this.url + 'auth/google', { data: data.getAuthResponse().id_token }).pipe(
          tap(() => this.storeUserType('google')),
          tap((response) => this.ngZone.run(() => this.handleSuccess(response))),
        ),
      ),
    );
  }

  public redirectToTransLogin(): void {
    this.transService.redirectToLogin();
  }

  public loginWithTransCode(code: string): Observable<any> {
    return this.http
      .post(this.url + 'auth/transid', {
        data: {
          code: code,
          redirectUri: this.transService.getRedirectUrl(),
        },
      })
      .pipe(
        tap(() => this.storeUserType('trans')),
        tap((response) => {
          this.transService.clearStoredRedirectUrl();
          this.ngZone.run(() => this.handleSuccess(response));
        }),
        catchError((error) => {
          this.transService.clearStoredRedirectUrl();
          return this.handleError(error);
        }),
      );
  }

  public loginWithPartnerCode(code: string): Observable<any> {
    return this.http.post(this.url + 'auth/exchange/code/login', { data: code }).pipe(
      tap(() => this.storeUserType('partner')),
      tap((data) => this.handleSuccess(data)),
      catchError((error) => this.handleError(error)),
    );
  }

  public changePassword(changePasswordData): Observable<any> {
    return this.http.post(this.url + 'auth/password/change', { data: changePasswordData }).pipe(
      tap(() => this.handleChangePasswordSuccess()),
      catchError((error) => this.handleError(error)),
    );
  }

  public requestPasswordReset(passwordResetRequestData): Observable<any> {
    return this.http
      .post(this.url + 'auth/password/reset/email', {
        data: passwordResetRequestData,
      })
      .pipe(
        tap(() => this.handleRequestPasswordResetSuccess()),
        catchError((error) => this.handleError(error)),
      );
  }

  public resetPassword(resetData): Observable<any> {
    return this.http.post(this.url + 'auth/password/reset', { data: resetData }).pipe(
      tap(() => this.handleRequestPasswordResetSuccess()),
      catchError((error) => this.handleError(error)),
    );
  }

  public register(registerData): Observable<any> {
    return this.http
      .post(this.url + 'auth/register', { data: registerData })
      .pipe(catchError((error) => this.handleError(error)));
  }

  public validatePasswordResetCode(code): Observable<any> {
    return this.http
      .post(this.url + 'auth/password/reset/code/validate', {
        data: { code: code },
      })
      .pipe(
        tap(() => this.handleRequestPasswordResetSuccess()),
        catchError((error) => this.handleError(error)),
      );
  }

private extractLanguageCode(url: string): string | null {
  const regex = /^\/([^/]+)\//;
  const match = regex.exec(url);
  return match ? match[1] : 'en';
}

  public validateRegisterCode(code): Observable<any> {
    const lclStr = localStorage.getItem('emailVerificationStateUrl');
    const authEmailLang = this.extractLanguageCode(lclStr);
    localStorage.removeItem('emailVerificationStateUrl');
    const body = { data: { code: code, lang: authEmailLang }};
    return this.http
      .post(this.url + 'auth/verification', body)
      .pipe(catchError((error) => this.handleError(error)));
  }

  public sendVerificationEmail(email): Observable<any> {
    return this.http
      .post(this.url + 'auth/verification/email', {
        data: {
          email: email,
          lang: this.translate.currentLang,
        },
      })
      .pipe(catchError((error) => this.handleError(error)));
  }

  private handleRequestPasswordResetSuccess(): void {
    return;
  }

  private handleSuccess(data): void {
    localStorage.token = data.token;
    this.navigationService.navigateToHomepage();
  }

  private handleSuccessReturn(data: unknown) {
    return data;
  }

  private handleChangePasswordSuccess(): void {
    return;
  }

  private handleError(error: HttpErrorResponse): Observable<any> {
    return throwError(error);
  }
}
